import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { 
  FileText, Clock, ChevronRight, Tag, Sparkles, 
  TrendingUp, Code, CheckCircle, Server, Rocket,
  AlertTriangle, Calendar, User, GitBranch, History
} from 'lucide-react';
import { 
  RELEASE_NOTES, 
  DOC_VERSION, 
  getLatestRelease 
} from '../../data/governanceContent';

// Icon mapping for release note categories
const CATEGORY_ICONS = {
  'New Features': Sparkles,
  'Improvements': TrendingUp,
  'Technical': Code,
  'Testing': CheckCircle,
  'Infrastructure': Server,
  'Core Features': Rocket,
  'Bug Fixes': CheckCircle,
  'Security': AlertTriangle
};

/**
 * ReleaseNotesSection - Displays all release notes with filtering
 */
export function ReleaseNotesSection({ onNavigateToDoc }) {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [filterPortal, setFilterPortal] = useState('all');

  const latestRelease = getLatestRelease();
  const displayedRelease = selectedVersion 
    ? RELEASE_NOTES.find(r => r.version === selectedVersion) 
    : latestRelease;

  const getTypeColor = (type) => {
    switch (type) {
      case 'major': return 'bg-red-100 text-red-700 border-red-200';
      case 'minor': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'patch': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="release-notes-section">
      {/* Header with current version */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Release Notes</h2>
                <p className="text-white/80">Track changes, updates, and new features</p>
              </div>
            </div>
            <div className="text-right">
              <Badge className="bg-white/20 text-white border-0 text-lg px-4 py-1">
                v{DOC_VERSION.current}
              </Badge>
              <p className="text-white/70 text-sm mt-1">Current Version</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Version selector */}
      <div className="flex flex-wrap gap-2">
        {RELEASE_NOTES.map(release => (
          <Button
            key={release.version}
            variant={selectedVersion === release.version || (!selectedVersion && release.version === latestRelease.version) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedVersion(release.version === latestRelease.version ? null : release.version)}
            className="gap-2"
          >
            <Tag className="w-3 h-3" />
            v{release.version}
            {release.version === latestRelease.version && (
              <Badge variant="secondary" className="ml-1 text-xs">Latest</Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Selected release details */}
      {displayedRelease && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl flex items-center gap-3">
                  Version {displayedRelease.version}
                  <Badge className={getTypeColor(displayedRelease.type)}>
                    {displayedRelease.type}
                  </Badge>
                </CardTitle>
                <CardDescription className="mt-1">
                  {displayedRelease.title}
                </CardDescription>
              </div>
              <div className="text-right text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {displayedRelease.date}
                </div>
              </div>
            </div>

            {/* Highlights */}
            {displayedRelease.highlights && displayedRelease.highlights.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {displayedRelease.highlights.map((highlight, idx) => (
                  <Badge key={idx} variant="outline" className="bg-indigo-50">
                    {highlight}
                  </Badge>
                ))}
              </div>
            )}
          </CardHeader>

          <CardContent>
            {/* Change sections */}
            <Accordion type="multiple" defaultValue={displayedRelease.sections.map(s => s.category)}>
              {displayedRelease.sections.map((section, sectionIdx) => {
                const SectionIcon = CATEGORY_ICONS[section.category] || FileText;
                return (
                  <AccordionItem key={sectionIdx} value={section.category}>
                    <AccordionTrigger className="hover:no-underline">
                      <span className="flex items-center gap-2">
                        <SectionIcon className="w-4 h-4 text-indigo-600" />
                        {section.category}
                        <Badge variant="secondary" className="ml-2">
                          {section.changes.length}
                        </Badge>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pl-6">
                        {section.changes.map((change, changeIdx) => (
                          <div 
                            key={changeIdx} 
                            className="p-3 bg-gray-50 rounded-lg"
                          >
                            <h4 className="font-medium text-gray-900">{change.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{change.description}</p>
                            {change.portals && change.portals.length > 0 && (
                              <div className="flex gap-1 mt-2">
                                {change.portals.map(portal => (
                                  <Badge key={portal} variant="outline" className="text-xs capitalize">
                                    {portal}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {change.docs && change.docs.length > 0 && (
                              <div className="flex gap-1 mt-2">
                                {change.docs.map(doc => (
                                  <Button
                                    key={doc}
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-xs text-indigo-600"
                                    onClick={() => onNavigateToDoc?.(doc)}
                                  >
                                    <FileText className="w-3 h-3 mr-1" />
                                    View Docs
                                  </Button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {/* Breaking changes warning */}
            {displayedRelease.breaking && displayedRelease.breaking.length > 0 && (
              <Card className="mt-4 border-red-200 bg-red-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    Breaking Changes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {displayedRelease.breaking.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Deprecation notices */}
            {displayedRelease.deprecated && displayedRelease.deprecated.length > 0 && (
              <Card className="mt-4 border-amber-200 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                    <Clock className="w-4 h-4" />
                    Deprecated
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                    {displayedRelease.deprecated.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline view */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Version Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            {/* Timeline items */}
            <div className="space-y-6">
              {RELEASE_NOTES.map((release, idx) => (
                <div key={release.version} className="relative pl-10">
                  {/* Timeline dot */}
                  <div className={`absolute left-2 w-4 h-4 rounded-full border-2 ${
                    idx === 0 
                      ? 'bg-indigo-500 border-indigo-500' 
                      : 'bg-white border-gray-300'
                  }`} />
                  
                  {/* Content */}
                  <div 
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      (selectedVersion === release.version || (!selectedVersion && idx === 0))
                        ? 'bg-indigo-50 border border-indigo-200'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedVersion(release.version === latestRelease.version ? null : release.version)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">v{release.version}</span>
                        <Badge className={`${getTypeColor(release.type)} text-xs`}>
                          {release.type}
                        </Badge>
                      </div>
                      <span className="text-sm text-gray-500">{release.date}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{release.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * VersionBadge - Small badge showing document version
 */
export function VersionBadge({ docId, showHistory = false, onViewHistory }) {
  const versionInfo = docId ? require('../../data/governanceContent').DOC_VERSIONS[docId] : null;
  
  if (!versionInfo) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="gap-1">
        <GitBranch className="w-3 h-3" />
        v{versionInfo.currentVersion}
      </Badge>
      {showHistory && versionInfo.versions && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 text-xs"
          onClick={onViewHistory}
        >
          <History className="w-3 h-3 mr-1" />
          History
        </Button>
      )}
    </div>
  );
}

/**
 * DocVersionHistory - Shows version history for a document
 */
export function DocVersionHistory({ docId, isOpen, onClose }) {
  const versionInfo = docId ? require('../../data/governanceContent').DOC_VERSIONS[docId] : null;

  if (!isOpen || !versionInfo) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md max-h-[80vh] overflow-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Version History
          </CardTitle>
          <CardDescription>
            Document: {docId}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {versionInfo.versions.map((version, idx) => (
              <div 
                key={version.version}
                className={`p-3 rounded-lg ${idx === 0 ? 'bg-indigo-50 border border-indigo-200' : 'bg-gray-50'}`}
              >
                <div className="flex items-center justify-between">
                  <Badge variant={idx === 0 ? 'default' : 'outline'}>
                    v{version.version}
                  </Badge>
                  <span className="text-xs text-gray-500">{version.date}</span>
                </div>
                <p className="text-sm text-gray-700 mt-2">{version.summary}</p>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {version.author}
                </p>
              </div>
            ))}
          </div>
          <Button variant="outline" className="w-full mt-4" onClick={onClose}>
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default ReleaseNotesSection;
